/**
 * Drama API namespace (§9.0). Wraps the shared axios client from `../api.ts`
 * (which already configures BASE_URL + auth interceptor + 401 refresh).
 *
 * Endpoint paths mirror tools/drama_demo_server.py (and §8 of the design doc).
 * They are scoped under the same /api/v1 prefix that the main backend uses,
 * but the demo server currently exposes them at /api/* without the v1 segment.
 * To bridge both modes a single `BASE` constant is exported so callers don't
 * hardcode the prefix in components.
 */
import { api } from '../api'
import type {
  BgmTrack,
  CreateDramaProjectInput,
  DramaLang,
  DramaProject,
  DramaProjectDeletionImpact,
  DramaProjectDetail,
  DramaProjectListResponse,
  DramaCharacterMergeRequest,
  DramaCharacterSplitRequest,
  DramaSegment,
  DramaSegmentSearchPayload,
  DramaStoryOutline,
  EpisodeUploadResult,
  GenerateMixPlansResult,
  CreateRenderDraftResult,
  ExportRenderResult,
  LocalizedList,
  LocalizedText,
  MixBriefInput,
  PatchDramaProjectInput,
  PreprocessProgress,
  RenderEditSpec,
  StartPreprocessResult,
} from '@/types/drama'

// ---------------------------------------------------------------------------
// Bilingual helpers — outline prose fields may be a plain string (legacy
// outlines) or an {en, zh} object (current bilingual prompt). These resolve a
// value for the requested language with graceful fallback to the other
// language, so both old and new data render.
// ---------------------------------------------------------------------------
export function pickLocalized(value: LocalizedText | undefined, lang: DramaLang): string {
  if (value == null) return ''
  if (typeof value === 'string') return value
  return value[lang] ?? value[lang === 'en' ? 'zh' : 'en'] ?? ''
}

export function pickLocalizedList(value: LocalizedList | undefined, lang: DramaLang): string[] {
  if (value == null) return []
  if (Array.isArray(value)) return value
  return value[lang] ?? value[lang === 'en' ? 'zh' : 'en'] ?? []
}

/** True when the value actually carries text for the given language (used to
 *  decide whether the language toggle is meaningful for this outline). */
export function hasLocalized(value: LocalizedText | LocalizedList | undefined, lang: DramaLang): boolean {
  if (value == null) return false
  if (typeof value === 'string') return value.length > 0
  if (Array.isArray(value)) return value.length > 0
  const v = value[lang]
  return Array.isArray(v) ? v.length > 0 : !!v
}

// Demo server uses /api/*; production backend will be /api/v1/drama-projects.
// Switch via VITE_DRAMA_API_PREFIX if needed.
const PREFIX = (import.meta.env.VITE_DRAMA_API_PREFIX as string | undefined) ?? '/drama-projects'
const STANDALONE = (import.meta.env.VITE_DRAMA_STANDALONE_BASE as string | undefined) ?? ''
// Note: when running against tools/drama_demo_server.py on port 8090, set
// VITE_DRAMA_STANDALONE_BASE=http://localhost:8090/api and VITE_DRAMA_API_PREFIX=/projects.

function url(path: string): string {
  return STANDALONE ? `${STANDALONE}${PREFIX}${path}` : `${PREFIX}${path}`
}

// ---------------------------------------------------------------------------
// Project CRUD
// ---------------------------------------------------------------------------

export const dramaApi = {
  async listProjects(): Promise<DramaProjectListResponse> {
    const res = await api.client.get<DramaProjectListResponse>(url(''))
    return res.data
  },

  async getProjectDetail(projectId: string): Promise<DramaProjectDetail> {
    const res = await api.client.get<DramaProjectDetail>(url(`/${projectId}/detail`))
    return res.data
  },

  async createProject(payload: CreateDramaProjectInput): Promise<DramaProject> {
    const res = await api.client.post(url(''), payload)
    return res.data as DramaProject
  },

  async patchProject(projectId: string, payload: PatchDramaProjectInput): Promise<DramaProject> {
    const res = await api.client.patch<DramaProject>(url(`/${projectId}`), payload)
    return res.data
  },

  async getDeletionImpact(projectId: string): Promise<DramaProjectDeletionImpact> {
    const res = await api.client.get<DramaProjectDeletionImpact>(url(`/${projectId}/deletion-impact`))
    return res.data
  },

  async deleteProject(projectId: string): Promise<{ deleted: boolean; impact: DramaProjectDeletionImpact | null }> {
    const res = await api.client.delete(url(`/${projectId}`))
    return res.data
  },

  // -------------------------------------------------------------------
  // Episode upload (multipart, §5.1)
  // -------------------------------------------------------------------

  async uploadEpisode(
    projectId: string,
    episodeNumber: number,
    file: File,
    language?: string | null,
    onProgress?: (loaded: number, total: number) => void,
  ): Promise<EpisodeUploadResult> {
    const form = new FormData()
    form.append('episode_number', String(episodeNumber))
    form.append('file', file)
    if (language) form.append('language', language)

    const res = await api.client.post<EpisodeUploadResult>(
      url(`/${projectId}/episodes/upload`),
      form,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 30 * 60 * 1000,
        onUploadProgress: (event) => {
          if (onProgress && event.total) {
            onProgress(event.loaded, event.total)
          }
        },
      },
    )
    return res.data
  },

  async deleteEpisode(projectId: string, episodeNumber: number): Promise<{ deleted: boolean; episode_number: number }> {
    const res = await api.client.delete(url(`/${projectId}/episodes/${episodeNumber}`))
    return res.data
  },

  // [FIX-2026-06-07] Re-analyse a ready episode: clears its segments + resets it
  // to pending so the next "Start AI analysis" re-cuts it with the current logic.
  async reprocessEpisode(
    projectId: string,
    episodeNumber: number,
  ): Promise<{ ok: boolean; episode_number: number; cleared_segments: number; status: string }> {
    const res = await api.client.post(url(`/${projectId}/episodes/${episodeNumber}/reprocess`), {})
    return res.data
  },

  async reorderEpisodes(projectId: string, order: number[]): Promise<{ reordered: boolean; order: number[] }> {
    const res = await api.client.post(url(`/${projectId}/episodes/reorder`), { order })
    return res.data
  },

  episodeFileUrl(projectId: string, episodeNumber: number): string {
    return url(`/${projectId}/episodes/${episodeNumber}/file`)
  },

  episodeThumbnailUrl(projectId: string, episodeNumber: number): string {
    return url(`/${projectId}/episodes/${episodeNumber}/thumbnail`)
  },

  characterAvatarUrl(projectId: string, characterCode: string, version?: number): string {
    const base = url(`/${projectId}/characters/${characterCode}/avatar`)
    // `version` busts the AuthedImage blob cache after a custom upload.
    return version ? `${base}?v=${version}` : base
  },

  // [REFACTOR-2026-06-06] BGM track library (曲目库): built-in catalog + the
  // project's uploaded tracks.
  async bgmLibrary(projectId: string): Promise<BgmTrack[]> {
    const res = await api.client.get<{ tracks: BgmTrack[] }>(url(`/${projectId}/bgm/library`))
    return res.data.tracks ?? []
  },

  // axios-relative URL for auditioning a track (fetch via AuthedAudio, since the
  // endpoint sits behind JWT auth — see AuthedVideo).
  bgmFileUrl(projectId: string, trackId: string): string {
    return url(`/${projectId}/bgm/file?track_id=${encodeURIComponent(trackId)}`)
  },

  // Upload a custom BGM track into the user library, with optional tags.
  // Returns the new library track so the UI can select it immediately.
  async uploadBgm(
    projectId: string,
    file: File,
    opts?: { tags?: string[]; name?: string },
  ): Promise<BgmTrack> {
    const form = new FormData()
    form.append('file', file)
    if (opts?.tags?.length) form.append('tags', opts.tags.join(','))
    if (opts?.name) form.append('name', opts.name)
    const res = await api.client.post<BgmTrack>(url(`/${projectId}/bgm/upload`), form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return res.data
  },

  // Edit a track's name and/or tags (works for seed and custom alike).
  async patchBgmTrack(
    projectId: string,
    trackId: string,
    patch: { name?: string; tags?: string[] },
  ): Promise<BgmTrack> {
    const res = await api.client.patch<BgmTrack>(
      url(`/${projectId}/bgm/${encodeURIComponent(trackId)}`),
      patch,
    )
    return res.data
  },

  async deleteBgmTrack(projectId: string, trackId: string): Promise<{ deleted: boolean }> {
    const res = await api.client.delete(url(`/${projectId}/bgm/${encodeURIComponent(trackId)}`))
    return res.data
  },

  async uploadCharacterAvatar(
    projectId: string,
    characterCode: string,
    file: File,
  ): Promise<{ ok: boolean; character_code: string }> {
    const form = new FormData()
    form.append('file', file)
    const res = await api.client.post(
      url(`/${projectId}/characters/${characterCode}/avatar`),
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    )
    return res.data
  },

  // -------------------------------------------------------------------
  // Preprocess control (M2.3 — explicit user trigger after upload review)
  // -------------------------------------------------------------------

  async startPreprocess(projectId: string, language?: string): Promise<StartPreprocessResult> {
    const res = await api.client.post<StartPreprocessResult>(
      url(`/${projectId}/preprocess/start`),
      language ? { language } : {},
    )
    return res.data
  },

  async getProgress(projectId: string): Promise<PreprocessProgress> {
    const res = await api.client.get<PreprocessProgress>(url(`/${projectId}/progress`))
    return res.data
  },

  // -------------------------------------------------------------------
  // Mix Plan generation (selection only — multiple proposals)
  // -------------------------------------------------------------------

  // [REFACTOR-2026-06-06] AI-write a creative brief from the project's story +
  // segments, optionally steered by the user's current text (`hint`).
  async suggestBrief(projectId: string, hint?: string): Promise<{ brief: string }> {
    const res = await api.client.post<{ brief: string }>(
      url(`/${projectId}/mix-plans/suggest-brief`),
      hint ? { hint } : {},
    )
    return res.data
  },

  // [REFACTOR-2026-06-06] Generate N segment-selection proposals (no render).
  // Each proposal is a read-only Mix Plan; polish + export happen on Render.
  async generateMixPlans(
    projectId: string,
    brief: string,
    opts: { targetDuration?: number; count?: number; mixBrief?: MixBriefInput } = {},
  ): Promise<GenerateMixPlansResult> {
    const body: Record<string, unknown> = { brief }
    if (opts.targetDuration != null) body.target_duration = opts.targetDuration
    if (opts.count != null) body.count = opts.count
    if (opts.mixBrief) body.mix_brief = opts.mixBrief
    const res = await api.client.post<GenerateMixPlansResult>(
      url(`/${projectId}/mix-plans`),
      body,
    )
    return res.data
  },

  // [REFACTOR-2026-06-06] File endpoints differ by backend:
  //  - Real API: under the project prefix → `/drama-projects/{pid}/renders/{rc}/file`
  //  - Demo server: flat under /api → `/renders/{pid}/{rc}/file`
  // The previous prefix-less helper 404'd against the real API (segment preview
  // / timeline player failed). Branch on STANDALONE to support both.
  renderFileUrl(projectId: string, renderCode: string): string {
    return STANDALONE
      ? `${STANDALONE}/renders/${projectId}/${renderCode}/file`
      : url(`/${projectId}/renders/${renderCode}/file`)
  },

  segmentFileUrl(projectId: string, segmentCode: string): string {
    return STANDALONE
      ? `${STANDALONE}/segments/${projectId}/${segmentCode}/file`
      : url(`/${projectId}/segments/${segmentCode}/file`)
  },

  // One exported final cut (Asset row). Stable per cut so <video src> is steady
  // across detail polls. detail.final_cuts[].file_url is the same path.
  finalCutFileUrl(projectId: string, cutId: string): string {
    return STANDALONE
      ? `${STANDALONE}/final-cuts/${projectId}/${cutId}/file`
      : url(`/${projectId}/final-cuts/${cutId}/file`)
  },

  // Remove a cut from the 成片 tab WITHOUT deleting the Asset Library video
  // (soft-hide). The mp4 +素材库 row stay; this drama view stops listing it.
  async removeFinalCut(projectId: string, cutId: string): Promise<void> {
    if (STANDALONE) return
    await api.client.delete(url(`/${projectId}/final-cuts/${cutId}`))
  },

  // -------------------------------------------------------------------
  // Story outline editing
  // -------------------------------------------------------------------

  async patchOutline(
    projectId: string,
    payload: Partial<DramaStoryOutline> & { is_user_edited?: boolean },
  ): Promise<DramaStoryOutline> {
    const res = await api.client.patch<DramaStoryOutline>(
      url(`/${projectId}/story-outline`),
      payload,
    )
    return res.data
  },

  async regenerateOutline(projectId: string): Promise<{ queued: boolean }> {
    const res = await api.client.post(url(`/${projectId}/story-outline/regenerate`), {})
    return res.data
  },

  // -------------------------------------------------------------------
  // Character management
  // -------------------------------------------------------------------

  async mergeCharacters(projectId: string, payload: DramaCharacterMergeRequest): Promise<void> {
    await api.client.post(url(`/${projectId}/characters/merge`), payload)
  },

  async splitCharacter(projectId: string, payload: DramaCharacterSplitRequest): Promise<void> {
    await api.client.post(url(`/${projectId}/characters/split`), payload)
  },

  async patchCharacter(
    projectId: string,
    characterCode: string,
    payload: { display_name?: string; aliases?: string[]; is_locked?: boolean; is_deleted?: boolean },
  ): Promise<void> {
    await api.client.patch(url(`/${projectId}/characters/${characterCode}`), payload)
  },

  // -------------------------------------------------------------------
  // Segment search
  // -------------------------------------------------------------------

  async searchSegments(
    projectId: string,
    payload: DramaSegmentSearchPayload,
  ): Promise<DramaSegment[]> {
    const res = await api.client.post<DramaSegment[]>(url(`/${projectId}/segments/search`), payload)
    return res.data
  },

  // -------------------------------------------------------------------
  // Mix Plan title edit (selection is read-only; regenerate to change it)
  // -------------------------------------------------------------------

  async patchEditPlan(
    projectId: string,
    planCode: string,
    payload: { title?: string },
  ): Promise<{ plan_code: string; title: string | null; status: string }> {
    const res = await api.client.patch(url(`/${projectId}/edit-plans/${planCode}`), payload)
    return res.data
  },

  async deleteEditPlan(
    projectId: string,
    planCode: string,
  ): Promise<{ deleted: boolean; plan_code: string }> {
    const res = await api.client.delete(url(`/${projectId}/edit-plans/${planCode}`))
    return res.data
  },

  // -------------------------------------------------------------------
  // Render workbench (draft → polish auto-save → export)
  // -------------------------------------------------------------------

  // [REFACTOR-2026-06-06] Create a draft Render from a plan (enters workbench).
  // A plan may be turned into many renders.
  async createRenderDraft(
    projectId: string,
    planCode: string,
  ): Promise<CreateRenderDraftResult> {
    const res = await api.client.post<CreateRenderDraftResult>(
      url(`/${projectId}/edit-plans/${planCode}/render`),
      {},
    )
    return res.data
  },

  // Auto-save the workbench edit_spec.
  async patchRender(
    projectId: string,
    renderCode: string,
    editSpec: RenderEditSpec,
  ): Promise<{ render_code: string; status: string; render_params: RenderEditSpec }> {
    const res = await api.client.patch(
      url(`/${projectId}/renders/${renderCode}`),
      { render_params: editSpec },
    )
    return res.data
  },

  // [FIX-2026-06-08] Best-effort flush of the edit_spec when the page is being
  // unloaded/navigated away — uses fetch(keepalive) so the request survives the
  // unload (a normal axios XHR would be cancelled, losing the last edit).
  patchRenderKeepalive(projectId: string, renderCode: string, editSpec: RenderEditSpec): void {
    try {
      const path = url(`/${projectId}/renders/${renderCode}`)
      const full = /^https?:\/\//.test(path) ? path : `${api.client.defaults.baseURL ?? ''}${path}`
      const token = localStorage.getItem('access_token')
      void fetch(full, {
        method: 'PATCH',
        keepalive: true,
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ render_params: editSpec }),
      })
    } catch {
      /* best effort */
    }
  },

  // Run FFmpeg to produce the final mp4.
  async exportRender(
    projectId: string,
    renderCode: string,
  ): Promise<ExportRenderResult> {
    const res = await api.client.post<ExportRenderResult>(
      url(`/${projectId}/renders/${renderCode}/export`),
      {},
    )
    return res.data
  },
}

// ---------------------------------------------------------------------------
// Shared react-query keys for invalidation
// ---------------------------------------------------------------------------
export const dramaQueryKeys = {
  all: ['drama'] as const,
  list: () => [...dramaQueryKeys.all, 'list'] as const,
  detail: (projectId: string) => [...dramaQueryKeys.all, 'detail', projectId] as const,
  progress: (projectId: string) => [...dramaQueryKeys.all, 'progress', projectId] as const,
  segments: (projectId: string) => [...dramaQueryKeys.all, 'segments', projectId] as const,
}
